import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowUpRight, Feather } from "lucide-react";

const PRAXIS_URL = "https://tacc-praxis.polsia.app";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const storyText = (s) => s.story || s.text || s.body || s.change || s.through || "";
const storyBy = (s) => s.byline || s.name || s.author || "Anonymous";

export const StoryRoomBridge = () => {
  const [stories, setStories] = useState([]);

  useEffect(() => {
    axios.get(`${API}/praxis/stories`)
      .then(({ data }) => setStories((data.items || []).filter((s) => storyText(s)).slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
  <section className="py-24 md:py-32 px-6 md:px-10" data-testid="story-room-bridge">
    <div className="max-w-[1440px] mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: [0.65, 0, 0.35, 1] }}
        className="relative overflow-hidden rounded-2xl bg-[#FBF1EA] text-[#F5EFEB] p-10 md:p-16"
      >
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: "radial-gradient(ellipse at 85% 20%, rgba(158,43,30,0.12), transparent 55%)" }}
        />
        <div className="relative grid grid-cols-12 gap-10 items-center">
          <div className="col-span-12 md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="w-8 h-px bg-[#9E2B1E]" />
              <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#9E2B1E]">
                TACC Praxis · The Story Room
              </span>
            </div>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.98] tracking-tight mb-5">
              The story is <span className="italic text-[#9E2B1E]">still becoming.</span>
            </h2>
            <p className="text-[#5C4F4A] text-base md:text-lg leading-relaxed max-w-xl mb-8">
              Our Story Room is a public gathering place for honest accounts — what you moved through,
              what it asked of you, and what is changing on the other side. Four movements. One story. Yours.
            </p>
            <div className="flex flex-wrap items-center gap-4">
              <a
                href={PRAXIS_URL}
                target="_blank"
                rel="noreferrer"
                data-testid="story-room-enter-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[#9E2B1E] hover:bg-[#7F2115] text-[#FBF1EA] px-7 py-3.5 text-sm font-medium transition-colors"
              >
                Enter the Story Room <ArrowUpRight size={16} />
              </a>
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8A7A73]">
                Reviewed before publication · Share only what you want public
              </span>
            </div>
          </div>
          <div className="col-span-12 md:col-span-5 hidden md:block">
            <div className="relative rounded-xl bg-[#F5EFEB] aspect-[4/3] flex items-center justify-center overflow-hidden">
              <div
                className="absolute inset-0"
                style={{ background: "radial-gradient(ellipse at 50% 60%, rgba(244,140,110,0.28), transparent 65%)" }}
              />
              <span className="relative font-display text-8xl text-[#F0937A]">T</span>
              <span className="absolute top-4 left-5 font-mono text-[9px] tracking-[0.3em] uppercase text-[#D9A79A]">Story Room</span>
              <Feather size={16} className="absolute bottom-4 right-5 text-[#D9A79A]" strokeWidth={1.4} />
            </div>
          </div>
        </div>
        <div className="relative mt-12 pt-10 border-t border-[#F5EFEB]/10" data-testid="praxis-stories-feed">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-[#9E2B1E]" />
            <span className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#9E2B1E]">Latest from the room</span>
          </div>
          {stories.length > 0 ? (
            <div className="grid grid-cols-12 gap-6">
              {stories.map((s, i) => (
                <blockquote key={storyText(s).slice(0, 60)} className="col-span-12 md:col-span-4" data-testid={`praxis-story-${i}`}>
                  <p className="font-display text-lg italic leading-relaxed text-[#3A2C27] mb-3">
                    “{storyText(s).slice(0, 220)}{storyText(s).length > 220 ? "…" : ""}”
                  </p>
                  <cite className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#8A7A73] not-italic">— {storyBy(s)}</cite>
                </blockquote>
              ))}
            </div>
          ) : (
            <p className="text-[#5C4F4A] text-sm" data-testid="praxis-stories-empty">
              The first story is still waiting.{" "}
              <a href={PRAXIS_URL} target="_blank" rel="noreferrer" className="text-[#9E2B1E] underline underline-offset-4">
                Yours could open the room.
              </a>
            </p>
          )}
        </div>
      </motion.div>
    </div>
  </section>
);
};
